import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { UserCircleIcon, ChevronDownIcon, KeyIcon, ArrowRightOnRectangleIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const UserDropdown = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { user, isAuthenticated, isAdmin, logout } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const styles = {
        container: {
            position: 'relative',
        },
        loginButton: {
            padding: '6px 14px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: '#007AFF',
            color: '#fff',
            fontSize: '0.85rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        },
        userButton: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
        },
        userIcon: {
            width: '22px',
            height: '22px',
            color: '#1D1D1F',
        },
        userName: {
            fontSize: '0.85rem',
            fontWeight: '500',
            color: '#1D1D1F',
            maxWidth: '100px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        chevron: {
            width: '14px',
            height: '14px',
            color: '#86868B',
            transition: 'transform 0.2s ease',
        },
        chevronOpen: {
            transform: 'rotate(180deg)',
        },
        dropdown: {
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: '160px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
            zIndex: 200,
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '12px 16px',
            border: 'none',
            backgroundColor: 'transparent',
            fontSize: '0.9rem',
            color: '#1D1D1F',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            textAlign: 'left',
        },
        menuItemHover: {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        menuIcon: {
            width: '18px',
            height: '18px',
            color: '#86868B',
        },
        divider: {
            height: '1px',
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            margin: '4px 0',
        },
        logoutItem: {
            color: '#FF3B30',
        },
        logoutIcon: {
            color: '#FF3B30',
        },
        adminIcon: {
            color: '#5856D6',
        },
    };

    const handleLogout = async () => {
        setIsOpen(false);
        await logout();
        navigate('/');
    };

    const handleChangePassword = () => {
        setIsOpen(false);
        navigate('/password-reset');
    };

    const handleAdminPanel = () => {
        setIsOpen(false);
        navigate('/admin');
    };

    if (!isAuthenticated) {
        return (
            <button
                style={styles.loginButton}
                onClick={() => navigate('/login')}
            >
                {t('auth.login')}
            </button>
        );
    }

    const displayName = user?.email?.split('@')[0] || t('auth.user');

    return (
        <div style={styles.container} ref={dropdownRef}>
            <button
                style={styles.userButton}
                onClick={() => setIsOpen(!isOpen)}
            >
                <UserCircleIcon style={styles.userIcon} />
                <span style={styles.userName}>{displayName}</span>
                <ChevronDownIcon style={{ ...styles.chevron, ...(isOpen ? styles.chevronOpen : {}) }} />
            </button>

            {isOpen && (
                <div style={styles.dropdown}>
                    {isAdmin && (
                        <>
                            <button
                                style={styles.menuItem}
                                onClick={handleAdminPanel}
                                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                                <Cog6ToothIcon style={{ ...styles.menuIcon, ...styles.adminIcon }} />
                                {t('auth.adminPanel')}
                            </button>
                            <div style={styles.divider} />
                        </>
                    )}
                    <button
                        style={styles.menuItem}
                        onClick={handleChangePassword}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        <KeyIcon style={styles.menuIcon} />
                        {t('auth.changePassword')}
                    </button>
                    <div style={styles.divider} />
                    <button
                        style={{ ...styles.menuItem, ...styles.logoutItem }}
                        onClick={handleLogout}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 59, 48, 0.08)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                        <ArrowRightOnRectangleIcon style={{ ...styles.menuIcon, ...styles.logoutIcon }} />
                        {t('auth.logout')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default UserDropdown;
